if (!customElements.get('read-more')) {
  class ReadMore extends HTMLElement {
    static HIDDEN_LINES_THRESHOLD = 3;
    static instanceCounter = 0;
    static ANIMATION_MS = 300;

    constructor() {
      super();
      this.toggle = this.toggle.bind(this);
      this.evaluate = this.evaluate.bind(this);
      this.debouncedEvaluate = debounce(
        this.evaluate,
        ON_CHANGE_DEBOUNCE_TIMER
      );
    }

    connectedCallback() {
      this.content = this.querySelector('[data-read-more-content]');
      this.button = this.querySelector('[data-read-more-toggle]');
      this.label = this.querySelector('[data-read-more-label]');

      if (!this.content || !this.button) return;

      this.isCollapsible = this.hasAttribute('data-collapsible');
      const minHiddenLines = parseInt(this.dataset.minHiddenLines, 10);
      this.minHiddenLines = Number.isNaN(minHiddenLines)
        ? ReadMore.HIDDEN_LINES_THRESHOLD
        : minHiddenLines;
      this.isExpanded = false;

      if (!this.content.id) {
        ReadMore.instanceCounter += 1;
        this.content.id = `ReadMoreContent-${ReadMore.instanceCounter}`;
      }
      this.button.setAttribute('aria-controls', this.content.id);

      this.button.addEventListener('click', this.toggle);
      window.addEventListener('resize', this.debouncedEvaluate);

      this.evaluate();

      if (document.fonts) {
        document.fonts.ready.then(() => {
          if (this.isConnected) this.evaluate();
        });
      }
    }

    disconnectedCallback() {
      this.endAnimation();
      if (this.button) {
        this.button.removeEventListener('click', this.toggle);
      }
      window.removeEventListener('resize', this.debouncedEvaluate);
    }

    get hiddenLineCount() {
      const styles = window.getComputedStyle(this.content);
      const fontSize = parseFloat(styles.fontSize);
      let lineHeight = parseFloat(styles.lineHeight);

      if (Number.isNaN(lineHeight)) {
        lineHeight = fontSize * 1.4;
      } else if (!styles.lineHeight.endsWith('px')) {
        lineHeight *= fontSize;
      }

      const hiddenHeight =
        this.content.scrollHeight - this.content.clientHeight;

      return Math.round(hiddenHeight / lineHeight);
    }

    evaluate() {
      if (this.isExpanded) return;

      this.content.classList.add('read-more__content--clamped');

      const showToggle = this.hiddenLineCount > this.minHiddenLines;

      if (!showToggle) {
        this.content.classList.remove('read-more__content--clamped');
      }

      this.button.hidden = !showToggle;
      this.notifyHeightChange();
    }

    toggle() {
      this.isExpanded = !this.isExpanded;
      this.button.setAttribute('aria-expanded', this.isExpanded);

      if (this.label) {
        this.label.textContent = this.isExpanded
          ? this.button.dataset.labelLess
          : this.button.dataset.labelMore;
      }

      if (this.isExpanded) {
        this.expand();
      } else {
        this.collapse();
      }

      if (this.isExpanded && !this.isCollapsible) {
        this.button.hidden = true;
        this.content.focus();
      }
    }

    expand() {
      const startHeight = this.content.offsetHeight;
      this.content.classList.remove('read-more__content--clamped');
      this.animateHeight(startHeight, this.content.scrollHeight, () =>
        this.notifyHeightChange()
      );
    }

    collapse() {
      const startHeight = this.content.offsetHeight;
      this.content.classList.add('read-more__content--clamped');
      const endHeight = this.content.offsetHeight;
      this.content.classList.remove('read-more__content--clamped');

      this.animateHeight(startHeight, endHeight, () => this.evaluate());
    }

    animateHeight(from, to, onDone) {
      this.endAnimation();

      const reducedMotion = window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches;

      if (reducedMotion || from === to) {
        onDone();
        return;
      }

      const content = this.content;
      content.style.overflow = 'hidden';
      content.style.height = `${from}px`;
      content.getBoundingClientRect();
      content.style.transition = `height ${ReadMore.ANIMATION_MS}ms ease`;
      content.style.height = `${to}px`;

      const finish = () => {
        this.endAnimation();
        onDone();
      };
      const onTransitionEnd = (event) => {
        if (event.target === content && event.propertyName === 'height') {
          finish();
        }
      };

      content.addEventListener('transitionend', onTransitionEnd);
      const timeoutId = setTimeout(finish, ReadMore.ANIMATION_MS + 100);
      this.endAnimation = () => {
        clearTimeout(timeoutId);
        content.removeEventListener('transitionend', onTransitionEnd);
        content.style.height = '';
        content.style.transition = '';
        content.style.overflow = '';
        this.endAnimation = ReadMore.prototype.endAnimation;
      };
    }

    endAnimation() {}

    notifyHeightChange() {
      this.dispatchEvent(new Event('read-more:toggled', { bubbles: true }));
    }
  }

  customElements.define('read-more', ReadMore);
}
